import React, { useContext, useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useLocation } from 'react-router-dom'

import { useRemoteStrings } from '@acorns/web-utils'
import { compose } from 'recompose'

import Nav from 'components/Nav'
import { ActionType, PageName } from 'generated/types'
import ReferralReminderCtaArrow from 'pages/MOC/assets/referral-reminder-cta-arrow-image.svg'
import ReferralReminderImage from 'pages/MOC/assets/referral-reminder-image.svg'
import { NextButtonTappedEvent, ScreenViewedEvent } from 'pages/MOC/ReferralReminder/events'
import {
  Container,
  Content,
  ItemContainer,
  LineContainer,
  LineText,
  StepNumber,
  StepText,
  StyledButton,
  StyledButtonImage,
  StyledImage,
  StyledLayout,
  StyledMobileImage,
  Text,
  Title,
} from 'pages/MOC/ReferralReminder/styled-components'
import { TestIDs } from 'test/utils/testIds'
import theme from 'theme'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getFlow } from 'utils/FlowProvider/utils'
import { FunnelContext } from 'utils/FunnelContext'

type Props = WithFlowRouterProps

type LineItemProps = {
  item: string
  testId: string
  index: number
}

export const LineItem = ({ item, testId, index }: LineItemProps) => (
  <LineContainer data-testid={testId}>
    <StepNumber>
      <StepText>{index + 1}</StepText>
    </StepNumber>
    <LineText>{item}</LineText>
  </LineContainer>
)

const enhance = compose<Props, {}>(withFlowRouter)

const MOCReferralReminder: React.FunctionComponent<Props> = ({ route }) => {
  const location = useLocation<{ isReferred?: boolean }>()
  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const { getRemoteString } = useRemoteStrings()
  const isMobile = useMediaQuery({ maxWidth: `${theme.breakpoints.sm}px` })

  const ctaTitle = getRemoteString({ key: 'registration.moc_referral_reminder.cta' })

  useEffect(() => {
    if (location?.state?.isReferred === false) {
      isDynamicFunnelEnabled
        ? routeToPage(PageName.MocReferralReminder, ActionType.Continue)
        : route({ from: FlowRoute.MOCReferralReminder, action: 'continue' })
    } else {
      trackEvent('Screen Viewed', {
        eventData: ScreenViewedEvent,
        flow: getFlow(),
      })
    }
  }, [])

  const items = [
    getRemoteString({ key: 'registration.moc_referral_reminder.first.desc' }),
    getRemoteString({ key: 'registration.moc_referral_reminder.second.desc' }),
  ]

  const handleContinue = async () => {
    trackEvent('Button Tapped', {
      eventData: NextButtonTappedEvent(ctaTitle),
      flow: getFlow(),
    })

    isDynamicFunnelEnabled
      ? routeToPage(PageName.MocReferralReminder, ActionType.Continue)
      : route({ from: FlowRoute.MOCReferralReminder, action: 'continue' })
  }

  return (
    <>
      <Nav />
      <StyledLayout>
        <Container>
          <Content>
            <Title data-testid={TestIDs.MOC_REFERRAL_REMINDER.TITLE}>
              {getRemoteString({ key: 'registration.moc_referral_reminder.title' })}
            </Title>
            <Text data-testid={TestIDs.MOC_REFERRAL_REMINDER.TEXT}>
              {getRemoteString({ key: 'registration.moc_referral_reminder.body' })}
            </Text>
            <ItemContainer>
              {items.map((item, index) => (
                <LineItem
                  testId={`${TestIDs.MOC_REFERRAL_REMINDER.DESCRIPTION}_${index}`}
                  item={item}
                  key={`referral_reminder_step_${index}`}
                  index={index}
                />
              ))}
            </ItemContainer>
            <StyledButton
              data-testid={TestIDs.MOC_REFERRAL_REMINDER.NEXT_BUTTON}
              onPress={handleContinue}
              styleType="dark"
            >
              {ctaTitle}
              <StyledButtonImage src={ReferralReminderCtaArrow} />
            </StyledButton>
          </Content>
          {!isMobile && <StyledImage src={ReferralReminderImage} />}
        </Container>
      </StyledLayout>
      {isMobile && <StyledMobileImage src={ReferralReminderImage} />}
    </>
  )
}

export default enhance(MOCReferralReminder)
